import { caseManagementApi } from '../caseManagementApi';
import { PolicyView } from '../types/policyTypes';
import { PolicySaveEndpointRequest } from '../requests/policySaveRequest';
import { PolicyDeleteEndpointRequest } from '../requests/policyDeleteRequest';
import { PoliciesEndpointRequest } from '../requests/policiesRequest';

const policyEndpointsInjected = caseManagementApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get a single policy by ID
    // GET /policy?id=<id>
    getPolicy: builder.query<PolicyView, string>({
      query: (id) => ({
        url: 'policy',
        params: { id },
      }),
      providesTags: (result, error, id) => [{ type: 'Policy', id }],
    }),

    // Save/Update a policy
    // POST /Policy
    savePolicy: builder.mutation<PolicyView, PolicySaveEndpointRequest>({
      query: (body) => ({
        url: 'Policy',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => {
        const tags: any[] = ['PolicyList', { type: 'Policy', id: 'LIST' }];

        // Invalidate the specific policy that was saved (from the result)
        if (result?.id) {
          tags.push({ type: 'Policy', id: result.id });
        }

        // Also invalidate the policy that was being edited (from the request)
        if (arg.PolicyView?.id) {
          tags.push({ type: 'Policy', id: arg.PolicyView.id });
        }

        // Also invalidate all Policy cache entries to be extra sure
        tags.push('Policy');

        return tags;
      },
    }),

    // Delete a policy
    // DELETE /Policy
    deletePolicy: builder.mutation<boolean, PolicyDeleteEndpointRequest>({
      query: (body) => ({
        url: 'Policy',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Policy', id: arg.policyId },
        'PolicyList',
        'Policy',
      ],
    }),

    // Get all policies for a company
    // GET /Policies?company=<company>
    getPolicies: builder.query<PolicyView[], PoliciesEndpointRequest>({
      query: ({ company }) => ({
        url: 'Policies',
        params: { company },
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Policy' as const, id })), 'PolicyList']
          : ['PolicyList'],
    }),
  }),
  overrideExisting: false,
});

// Export enhanced API for slice extraReducers
export const policyEndpoints = policyEndpointsInjected;

export const {
  useGetPolicyQuery,
  useSavePolicyMutation,
  useDeletePolicyMutation,
  useGetPoliciesQuery,
} = policyEndpointsInjected;
